import { useEffect, useRef, useState } from 'react';
import { Copy, Share2, Twitter, Facebook, Linkedin, MessageCircle } from 'lucide-react';
import { trackShare, trackShareButtonClick, trackShareButtonImpression } from '@/lib/analytics';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/** ANALYTICS-034: per-channel UTM tags so inbound shared-link traffic shows up
 *  attributed to its source instead of as 'direct'. Enables k-factor tracking. */
type UtmChannel = 'whatsapp' | 'twitter' | 'facebook' | 'linkedin' | 'copy_link' | 'native';

function buildUtmQuery(channel: UtmChannel): string {
  const params = new URLSearchParams({
    utm_source: channel,
    utm_medium: 'share',
  });
  if (channel !== 'copy_link') params.set('utm_campaign', 'organic_share');
  return params.toString();
}

/** Build the OG-enriched share URL that social crawlers can read.
 *  Crawlers see proper og:title/image/description; humans get meta-refresh to the SPA.
 *  When `channel` is provided, appends ANALYTICS-034 UTM params for attribution. */
export function ogShareUrl(postId: string, channel?: UtmChannel): string {
  const base = `${API_URL}/og/post/${postId}`;
  if (!channel) return base;
  return `${base}?${buildUtmQuery(channel)}`;
}

/** SEO-913: OG share URL for static pages (vehicle specs, guides, comparisons).
 *  Routes to /og/page with title/url/category so social crawlers get a branded preview
 *  with dynamically generated OG image instead of a generic fallback. */
export function ogPageShareUrl(opts: { title: string; url: string; category?: string }, channel?: UtmChannel): string {
  const params = new URLSearchParams({
    title: opts.title,
    url: opts.url,
    ...(opts.category ? { category: opts.category } : {}),
  });
  if (channel) {
    params.set('utm_source', channel);
    params.set('utm_medium', 'share');
    if (channel !== 'copy_link') params.set('utm_campaign', 'organic_share');
  }
  return `${API_URL}/og/page?${params}`;
}

type ShareContext = {
  postId: string;
  title: string;
  url: string;
  category?: string;
  sourceId?: string;
};

// WhatsApp icon SVG (Lucide doesn't include it)
function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// Ticket-spec WhatsApp pre-fill: "Check out this on MotoWalla: {title} {url}"
const whatsAppText = (title: string, url: string) => `Check out this on MotoWalla: ${title} ${url}`;

type ShareSurface = 'detail_footer' | 'detail_sidebar' | 'detail_sticky';

function useShareActions({ postId, title, url, category, sourceId }: ShareContext, surface: ShareSurface) {
  const [copied, setCopied] = useState(false);

  // SEO-913: Non-UUID postIds (e.g. "cars/tata/sierra-ev") are static pages —
  // route them to /og/page with title/url/category for branded OG previews.
  const isPage = postId.includes('/');
  const pageOpts = { title, url, category };
  const getShareUrl = (ch: UtmChannel) => isPage ? ogPageShareUrl(pageOpts, ch) : ogShareUrl(postId, ch);
  const getBaseUrl = () => isPage ? ogPageShareUrl(pageOpts) : ogShareUrl(postId);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl('copy_link'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — fail silently
    }
    trackShare('copy', postId, title, category, sourceId);
    trackShareButtonClick(postId, 'copy', surface);
  };

  const shareToTwitter = () => {
    const shareUrl = getShareUrl('twitter');
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'noopener,noreferrer'
    );
    trackShare('twitter', postId, title, category, sourceId);
    trackShareButtonClick(postId, 'twitter', surface);
  };

  const shareToWhatsApp = () => {
    const shareUrl = getShareUrl('whatsapp');
    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(whatsAppText(title, shareUrl))}`,
      '_blank',
      'noopener,noreferrer'
    );
    trackShare('whatsapp', postId, title, category, sourceId);
    trackShareButtonClick(postId, 'whatsapp', surface);
  };

  const shareToFacebook = () => {
    const shareUrl = getShareUrl('facebook');
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'noopener,noreferrer'
    );
    trackShare('facebook', postId, title, category, sourceId);
    trackShareButtonClick(postId, 'facebook', surface);
  };

  const shareToLinkedIn = () => {
    const shareUrl = getShareUrl('linkedin');
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'noopener,noreferrer'
    );
    trackShare('linkedin', postId, title, category, sourceId);
    trackShareButtonClick(postId, 'linkedin', surface);
  };

  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  const nativeShare = async () => {
    try {
      await navigator.share({ title, text: title, url: getBaseUrl() });
      trackShare('native', postId, title, category, sourceId);
      trackShareButtonClick(postId, 'native', surface);
    } catch {
      // User cancelled or share failed — ignore
    }
  };

  return { copied, copyLink, shareToTwitter, shareToWhatsApp, shareToFacebook, shareToLinkedIn, canNativeShare, nativeShare };
}

type Variant = 'sidebar' | 'inline' | 'sticky';

export function ShareBar(props: ShareContext & { variant?: Variant }) {
  const variant = props.variant ?? 'inline';
  const surface: ShareSurface = variant === 'sidebar' ? 'detail_sidebar' : 'detail_footer';
  const { copied, copyLink, shareToTwitter, shareToWhatsApp, shareToFacebook, shareToLinkedIn, canNativeShare, nativeShare } = useShareActions(props, surface);
  const impressionRef = useRef<HTMLDivElement>(null);

  // ANALYTICS-035: Track share bar impression on visibility
  useEffect(() => {
    const el = impressionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackShareButtonImpression(props.postId, surface);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [props.postId, surface]);

  // Sidebar card (sits in right column)
  if (variant === 'sidebar') {
    return (
      <div ref={impressionRef} className="rounded-2xl border border-border-primary bg-bg-secondary p-5">
        <h3 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
          <Share2 className="h-4 w-4" />
          Share this post
        </h3>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={shareToWhatsApp}
            className="flex items-center justify-center gap-2 rounded-xl bg-[#25D366]/10 px-3 py-2.5 text-sm font-medium text-[#25D366] transition-colors hover:bg-[#25D366]/20"
          >
            <WhatsAppIcon className="h-4 w-4" />
            WhatsApp
          </button>
          <button
            onClick={shareToTwitter}
            className="flex items-center justify-center gap-2 rounded-xl bg-[#1DA1F2]/10 px-3 py-2.5 text-sm font-medium text-[#1DA1F2] transition-colors hover:bg-[#1DA1F2]/20"
          >
            <Twitter className="h-4 w-4" />
            Twitter
          </button>
          <button
            onClick={shareToFacebook}
            className="flex items-center justify-center gap-2 rounded-xl bg-[#4267B2]/10 px-3 py-2.5 text-sm font-medium text-[#4267B2] transition-colors hover:bg-[#4267B2]/20"
          >
            <Facebook className="h-4 w-4" />
            Facebook
          </button>
          <button
            onClick={shareToLinkedIn}
            className="flex items-center justify-center gap-2 rounded-xl bg-[#0A66C2]/10 px-3 py-2.5 text-sm font-medium text-[#0A66C2] transition-colors hover:bg-[#0A66C2]/20"
          >
            <Linkedin className="h-4 w-4" />
            LinkedIn
          </button>
          <button
            onClick={copyLink}
            className="col-span-2 flex items-center justify-center gap-2 rounded-xl border border-border-primary bg-bg-primary px-3 py-2.5 text-sm font-medium text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
          >
            <Copy className="h-4 w-4" />
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
          {canNativeShare && (
            <button
              onClick={nativeShare}
              className="col-span-2 flex items-center justify-center gap-2 rounded-xl bg-primary-500/10 px-3 py-2.5 text-sm font-medium text-primary-400 transition-colors hover:bg-primary-500/20"
            >
              <Share2 className="h-4 w-4" />
              More sharing options
            </button>
          )}
        </div>
      </div>
    );
  }

  // Inline horizontal bar — for SEO editorial pages
  return (
    <div className="space-y-3">
      <div ref={impressionRef} className="flex flex-wrap items-center gap-2 rounded-xl border border-border-primary bg-bg-secondary px-4 py-3">
        <span className="mr-1 flex items-center gap-1.5 text-sm font-medium text-text-secondary">
          <Share2 className="h-4 w-4" />
          Share
        </span>
        <button
          type="button"
          onClick={shareToWhatsApp}
          className="flex items-center gap-1.5 rounded-lg bg-[#25D366]/10 px-3 py-1.5 text-sm font-medium text-[#25D366] transition-colors hover:bg-[#25D366]/20"
          title="Share on WhatsApp"
        >
          <WhatsAppIcon className="h-4 w-4" />
          WhatsApp
        </button>
        <button
          type="button"
          onClick={shareToTwitter}
          className="flex items-center gap-1.5 rounded-lg bg-[#1DA1F2]/10 px-3 py-1.5 text-sm font-medium text-[#1DA1F2] transition-colors hover:bg-[#1DA1F2]/20"
          title="Share on Twitter"
        >
          <Twitter className="h-4 w-4" />
          Twitter
        </button>
        <button
          type="button"
          onClick={shareToLinkedIn}
          className="flex items-center gap-1.5 rounded-lg bg-[#0A66C2]/10 px-3 py-1.5 text-sm font-medium text-[#0A66C2] transition-colors hover:bg-[#0A66C2]/20"
          title="Share on LinkedIn"
        >
          <Linkedin className="h-4 w-4" />
          LinkedIn
        </button>
        <button
          type="button"
          onClick={copyLink}
          className="flex items-center gap-1.5 rounded-lg border border-border-primary bg-bg-primary px-3 py-1.5 text-sm font-medium text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
          title={copied ? 'Copied!' : 'Copy link'}
        >
          <Copy className="h-4 w-4" />
          {copied ? 'Copied!' : 'Copy link'}
        </button>
        {canNativeShare && (
          <button
            type="button"
            onClick={nativeShare}
            className="flex items-center gap-1.5 rounded-lg bg-primary-500/10 px-3 py-1.5 text-sm font-medium text-primary-400 transition-colors hover:bg-primary-500/20"
            title="More sharing options"
          >
            <Share2 className="h-4 w-4" />
            Share
          </button>
        )}
      </div>
      {/* MON-012: Prominent WhatsApp CTA — India-specific sharing callout */}
      <div className="rounded-2xl border border-[#25D366]/20 bg-gradient-to-br from-[#25D366]/5 to-[#128C7E]/5 p-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#25D366]/15">
            <WhatsAppIcon className="h-4.5 w-4.5 text-[#25D366]" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Share on WhatsApp</h3>
            <p className="text-xs text-text-tertiary">India's #1 way to share articles</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            const isPage = props.postId.includes('/');
            const pageOpts = { title: props.title, url: props.url, category: props.category };
            const shareUrl = isPage ? ogPageShareUrl(pageOpts, 'whatsapp') : ogShareUrl(props.postId, 'whatsapp');
            window.open(
              `https://api.whatsapp.com/send?text=${encodeURIComponent(whatsAppText(props.title, shareUrl))}`,
              '_blank',
              'noopener,noreferrer',
            );
            trackShare('whatsapp', props.postId, props.title, props.category, props.sourceId);
            trackShareButtonClick(props.postId, 'whatsapp', 'whatsapp_cta');
          }}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#22c35e] active:scale-[0.98]"
          aria-label="Share this article on WhatsApp"
        >
          <WhatsAppIcon className="h-4 w-4" />
          Share via WhatsApp
          <MessageCircle className="h-3.5 w-3.5 ml-1 opacity-60" />
        </button>
      </div>
    </div>
  );
}

export function StickyShareBar(props: ShareContext) {
  const { copied, copyLink, shareToTwitter, shareToWhatsApp, canNativeShare, nativeShare } = useShareActions(props, 'detail_sticky');
  const [visible, setVisible] = useState(false);
  const impressionFired = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ANALYTICS-035: Fire impression once when sticky bar first becomes visible
  useEffect(() => {
    if (visible && !impressionFired.current) {
      trackShareButtonImpression(props.postId, 'detail_sticky');
      impressionFired.current = true;
    }
  }, [visible, props.postId]);

  if (!visible) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-2xl border border-border-primary bg-bg-secondary/95 backdrop-blur-md px-4 py-2.5 shadow-xl shadow-black/20 transition-all duration-300">
      <button
        onClick={shareToWhatsApp}
        className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#25D366]/10 text-[#25D366] transition-colors hover:bg-[#25D366]/20"
        title="Share on WhatsApp"
      >
        <WhatsAppIcon className="h-5 w-5" />
      </button>
      <button
        onClick={shareToTwitter}
        className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1DA1F2]/10 text-[#1DA1F2] transition-colors hover:bg-[#1DA1F2]/20"
        title="Share on Twitter"
      >
        <Twitter className="h-5 w-5" />
      </button>
      <button
        onClick={copyLink}
        className="flex h-10 w-10 items-center justify-center rounded-xl border border-border-primary bg-bg-primary text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
        title={copied ? 'Copied!' : 'Copy link'}
      >
        {copied ? (
          <span className="text-xs font-bold text-green-400">&#10003;</span>
        ) : (
          <Copy className="h-5 w-5" />
        )}
      </button>
      {canNativeShare && (
        <button
          onClick={nativeShare}
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500/10 text-primary-400 transition-colors hover:bg-primary-500/20"
          title="More sharing options"
        >
          <Share2 className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
